import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const EM_DASH = '—';
const MAX_MONTHS_VISUAL = 18;

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const AssetsRequiringAttentionWidget = ({ onAssetClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const rows = data?.assetsRequiringAttention || [];

  const summary = useMemo(() => {
    const overdue = rows.filter((a) => (a?.remainingLifespanMonths ?? 0) <= 0).length;
    const expiring = rows.filter((a) => (a?.remainingLifespanMonths ?? 0) > 0).length;
    return { overdue, expiring };
  }, [rows]);

  const isEmpty = rows.length === 0;

  return (
    <ReportWidget
      title={
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap w-100">
          <div>
            <span>{t('business.inventory.assets_requiring_attention', 'Assets Requiring Attention')}</span>
            <div className="text-muted small fw-normal">
              {t('business.inventory.assets_requiring_attention_hint', 'Lifespan below 18 months')}
            </div>
          </div>
          <div className="d-flex gap-2">
            {summary.overdue > 0 && (
              <span
                className="fw-medium"
                style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.8rem', backgroundColor: '#FEE2E2', color: '#B91C1C' }}
              >
                {t('business.inventory.summary_overdue', { count: summary.overdue, defaultValue: '{{count}} overdue' })}
              </span>
            )}
            {summary.expiring > 0 && (
              <span
                className="fw-medium"
                style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.8rem', backgroundColor: '#FEF3C7', color: '#92400E' }}
              >
                {t('business.inventory.summary_expiring', { count: summary.expiring, defaultValue: '{{count}} expiring' })}
              </span>
            )}
          </div>
        </div>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('business.inventory.no_assets_requiring_attention', 'No assets require attention right now.')}
      onRefresh={handleRefresh}
    >
      <div className="d-flex align-items-center text-uppercase text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
        <div style={{ flex: '0 0 25%', paddingLeft: 16 }}>{t('shared.asset', 'Asset')}</div>
        <div style={{ flex: 1, textAlign: 'right', paddingRight: 8 }}>← {t('business.inventory.overdue_label', 'Overdue')}</div>
        <div style={{ flex: '0 0 80px', textAlign: 'center' }}>{t('business.inventory.today', 'Today')}</div>
        <div style={{ flex: 1, paddingLeft: 8 }}>{t('business.inventory.remaining_label', 'Remaining')} →</div>
        <div style={{ flex: '0 0 100px', textAlign: 'right' }}>{t('shared.nbv', 'NBV')}</div>
      </div>

      <div className="d-flex flex-column gap-1">
        {rows.map((a) => {
          const rm = a?.remainingLifespanMonths ?? 0;
          const isOverdue = rm <= 0;
          const absM = Math.abs(rm);
          const fillPct = Math.min(absM / MAX_MONTHS_VISUAL, 1) * 100;
          const barColor = isOverdue ? '#DC3545' : '#F59E0B';
          const labelColor = isOverdue ? '#B91C1C' : '#92400E';
          const labelText = isOverdue
            ? t('business.inventory.months_overdue', { count: absM, defaultValue: '{{count}} months overdue' })
            : t('business.inventory.months_left', { count: absM, defaultValue: '{{count}} months left' });

          return (
            <div
              key={a.assetId}
              role="button"
              tabIndex={0}
              onClick={() => a?.assetId && onAssetClick && onAssetClick(a.assetId)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && a?.assetId && onAssetClick) onAssetClick(a.assetId);
              }}
              className="d-flex align-items-center"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                padding: '8px 0',
                cursor: a?.assetId ? 'pointer' : 'default',
                borderLeft: `4px solid ${barColor}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ flex: '0 0 25%', paddingLeft: 16, minWidth: 0 }}>
                <div className="fw-semibold text-truncate" style={{ color: '#2563EB' }} title={a.assetName || a.assetNumber || ''}>
                  {a.assetName || a.assetNumber || t('shared.n_a', 'N/A')}
                </div>
                <div className="text-muted small text-truncate">
                  {[a.assetName && a.assetNumber ? `#${a.assetNumber}` : null, a.locationName].filter(Boolean).join(' · ') || EM_DASH}
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-end" style={{ flex: 1, paddingRight: 4 }}>
                {isOverdue && (
                  <>
                    <span className="small fw-medium me-2" style={{ color: labelColor, whiteSpace: 'nowrap' }}>{labelText}</span>
                    <div style={{ height: 10, width: `${fillPct}%`, background: barColor, borderRadius: '5px 0 0 5px' }} />
                  </>
                )}
              </div>

              <div style={{ flex: '0 0 80px', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 1, height: 18, background: '#111827' }} />
              </div>

              <div className="d-flex align-items-center" style={{ flex: 1, paddingLeft: 4 }}>
                {!isOverdue && (
                  <>
                    <div style={{ height: 10, width: `${fillPct}%`, background: barColor, borderRadius: '0 5px 5px 0' }} />
                    <span className="small fw-medium ms-2" style={{ color: labelColor, whiteSpace: 'nowrap' }}>{labelText}</span>
                  </>
                )}
              </div>

              <div style={{ flex: '0 0 100px', paddingRight: 16, textAlign: 'right' }} className="text-muted">
                {a.netBookValue != null ? formatMoney(a.netBookValue, currency) : EM_DASH}
              </div>
            </div>
          );
        })}
      </div>
    </ReportWidget>
  );
};

export default AssetsRequiringAttentionWidget;
